/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Project Serialization & Schema Validation
   Versioned project schema (V1 & V2) with lossless forward migration.
   ═══════════════════════════════════════════════════════════════ */

import type { CanonicalGraph, DomainGraphState } from './types';
import { createDomainState } from './graph';

export interface AlterlyProjectSchemaV1 {
  readonly version: 1;
  readonly app: 'alterly';
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly modifiedAt: string;
  readonly graph: CanonicalGraph;
  readonly settings: {
    readonly previewFormat: 'image' | 'video' | 'data';
    readonly outputDimensions?: { readonly width: number; readonly height: number };
  };
  readonly assetManifest: Record<
    string,
    {
      readonly id: string;
      readonly mimeType: string;
      readonly source: 'embedded' | 'linked';
      readonly hash?: string;
    }
  >;
}

export interface AlterlyProjectSchemaV2 {
  readonly version: 2;
  readonly app: 'alterly';
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly modifiedAt: string;
  readonly graph: CanonicalGraph;
  readonly settings: {
    readonly previewFormat: 'image' | 'video' | 'data';
    readonly outputDimensions?: { readonly width: number; readonly height: number };
  };
  readonly assetManifest: Record<
    string,
    {
      readonly id: string;
      readonly mimeType: string;
      readonly source: 'embedded' | 'linked';
      readonly hash?: string;
    }
  >;
}

export type AnyAlterlyProjectSchema = AlterlyProjectSchemaV1 | AlterlyProjectSchemaV2;

/**
 * Migrates a V1 project document into a canonical V2 project format.
 */
export function migrateV1toV2(v1: AlterlyProjectSchemaV1): AlterlyProjectSchemaV2 {
  const canonicalV2: CanonicalGraph = {
    ...v1.graph,
    version: 2,
    frames: v1.graph.frames || {},
  };

  return {
    ...v1,
    version: 2,
    graph: canonicalV2,
  };
}

/**
 * Serializes current domain graph state into the canonical project document.
 * Automatically targets version 2 if Phase 2 features (frames, node bypass) are present,
 * ensuring no Phase 2 state is ever lost. Defaults to version 1 for legacy Phase 1 graphs.
 */
export function serializeProject(
  state: DomainGraphState,
  projectName?: string,
  targetVersion?: 1 | 2
): AnyAlterlyProjectSchema {
  const hasPhase2Features =
    (state.canonical.frames && Object.keys(state.canonical.frames).length > 0) ||
    Object.values(state.canonical.nodes).some((n) => n.bypassed !== undefined);

  const effectiveVersion: 1 | 2 =
    targetVersion !== undefined
      ? targetVersion
      : (hasPhase2Features ? 2 : 1);

  const now = new Date().toISOString();
  const canonicalWithFrames: CanonicalGraph = {
    ...state.canonical,
    version: effectiveVersion,
    frames: state.canonical.frames || {},
  };

  return {
    version: effectiveVersion,
    app: 'alterly',
    id: state.canonical.id,
    name: projectName || state.canonical.name || 'Untitled Studio Project',
    createdAt: new Date(state.canonical.createdAt).toISOString(),
    modifiedAt: now,
    graph: canonicalWithFrames,
    settings: {
      previewFormat: 'image',
      outputDimensions: { width: 1920, height: 1080 },
    },
    assetManifest: {},
  } as AnyAlterlyProjectSchema;
}

/**
 * Restores a domain graph state from a project JSON object or string with validation.
 * Backward compatible with Phase 1 (V1) projects through lossless migration.
 */
export function deserializeProject(raw: unknown): {
  success: boolean;
  state?: DomainGraphState;
  projectName?: string;
  migratedFromVersion?: number;
  error?: string;
} {
  try {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
      parsed = JSON.parse(raw);
    }

    if (!parsed || typeof parsed !== 'object') {
      return { success: false, error: 'Invalid project format: root is not an object.' };
    }

    const doc = parsed as Partial<AnyAlterlyProjectSchema>;

    if (doc.app !== 'alterly') {
      return {
        success: false,
        error: `Unrecognized application signature: '${doc.app}'. Expected 'alterly'.`,
      };
    }

    let targetDoc: AlterlyProjectSchemaV2;
    let migratedFrom: number | undefined;

    if (doc.version === 1) {
      targetDoc = migrateV1toV2(doc as AlterlyProjectSchemaV1);
      migratedFrom = 1;
    } else if (doc.version === 2) {
      targetDoc = doc as AlterlyProjectSchemaV2;
    } else {
      return {
        success: false,
        error: `Unsupported schema version: ${doc.version}. Supported versions: 1, 2.`,
      };
    }

    if (!targetDoc.graph || !targetDoc.graph.nodes || !targetDoc.graph.edges) {
      return { success: false, error: 'Project document is missing canonical graph nodes or edges.' };
    }

    const state = createDomainState(targetDoc.graph);

    return {
      success: true,
      state,
      projectName: targetDoc.name || 'Restored Project',
      migratedFromVersion: migratedFrom,
    };
  } catch (err) {
    return {
      success: false,
      error: `Failed to deserialize project: ${(err as Error).message}`,
    };
  }
}
